import { WINE_AVAILABILITY, type WineAvailability } from '@wineryxplorer/shared';
import { FormSection } from '../ui/FormSection';
import type { WineFormErrors } from './types';

export interface WineTechnicalFieldsProps {
  alcoholContent: string;
  onAlcoholContentChange: (value: string) => void;
  price: string;
  onPriceChange: (value: string) => void;
  volume: string;
  onVolumeChange: (value: string) => void;
  availability: WineAvailability;
  onAvailabilityChange: (value: WineAvailability) => void;
  errors: WineFormErrors;
  touched: Record<string, boolean>;
  onBlur: (field: string) => void;
  isSubmitting: boolean;
}

export const WineTechnicalFields = ({
  alcoholContent,
  onAlcoholContentChange,
  price,
  onPriceChange,
  volume,
  onVolumeChange,
  availability,
  onAvailabilityChange,
  errors,
  touched,
  onBlur,
  isSubmitting,
}: WineTechnicalFieldsProps) => (
  <FormSection title="Technical Details">
    {/* Alcohol Content */}
    <div>
      <label htmlFor="alcoholContent" className="block text-sm font-medium text-gray-700 mb-1">
        Alcohol Content (%)
      </label>
      <input
        id="alcoholContent"
        type="text"
        inputMode="decimal"
        value={alcoholContent}
        onChange={(e) => onAlcoholContentChange(e.target.value)}
        onBlur={() => onBlur('alcoholContent')}
        disabled={isSubmitting}
        placeholder="e.g., 13.5"
        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 ${
          touched.alcoholContent && errors.alcoholContent ? 'border-red-500' : 'border-gray-300'
        } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
      />
      {touched.alcoholContent && errors.alcoholContent && (
        <p className="mt-1 text-sm text-red-600">{errors.alcoholContent}</p>
      )}
    </div>

    {/* Price */}
    <div>
      <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
        Price (USD)
      </label>
      <div className="relative">
        <span className="absolute left-3 top-2 text-gray-500">$</span>
        <input
          id="price"
          type="text"
          inputMode="decimal"
          value={price}
          onChange={(e) => onPriceChange(e.target.value)}
          onBlur={() => onBlur('price')}
          disabled={isSubmitting}
          placeholder="0.00"
          className={`w-full pl-8 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 ${
            touched.price && errors.price ? 'border-red-500' : 'border-gray-300'
          } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        />
      </div>
      {touched.price && errors.price && <p className="mt-1 text-sm text-red-600">{errors.price}</p>}
    </div>

    {/* Volume */}
    <div>
      <label htmlFor="volume" className="block text-sm font-medium text-gray-700 mb-1">
        Volume (ml)
      </label>
      <input
        id="volume"
        type="text"
        inputMode="numeric"
        value={volume}
        onChange={(e) => onVolumeChange(e.target.value)}
        onBlur={() => onBlur('volume')}
        disabled={isSubmitting}
        placeholder="e.g., 750"
        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 ${
          touched.volume && errors.volume ? 'border-red-500' : 'border-gray-300'
        } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
      />
      {touched.volume && errors.volume && (
        <p className="mt-1 text-sm text-red-600">{errors.volume}</p>
      )}
    </div>

    {/* Availability */}
    <div>
      <label htmlFor="availability" className="block text-sm font-medium text-gray-700 mb-1">
        Availability
      </label>
      <select
        id="availability"
        value={availability}
        onChange={(e) => onAvailabilityChange(e.target.value as WineAvailability)}
        disabled={isSubmitting}
        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 ${
          isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''
        }`}
      >
        {WINE_AVAILABILITY.map((avail) => (
          <option key={avail} value={avail}>
            {avail.charAt(0).toUpperCase() + avail.slice(1)}
          </option>
        ))}
      </select>
    </div>
  </FormSection>
);
